// react
import { useCallback, useEffect, useMemo, useState } from 'react';

// mui
import { Chip, Button, Stack, Typography } from '@mui/material';

// components
import { Avatar } from '@karehero/llama/components/atoms/Avatar/Avatar';
import { Dialog } from '@karehero/llama/components/atoms/Dialog/Dialog';
import { createFieldHelper } from '@karehero/llama/components/organisms/EditorFields/fieldHelper';
import { Editor } from '@karehero/llama/components/organisms/Editor/Editor';
import AdminTablePage from '@karehero/llama/pwa/templates/AdminTablePage/AdminTablePage';
import DataTable, {
  GridColDef,
  GridRenderCellParams,
} from '@karehero/llama/pwa/organisms/DataTable/DataTable';

// react-router
import { useNavigate } from 'react-router-dom';

// store
import { useDispatch } from 'react-redux';
import { useGetAllQuery } from 'store/api/careCircle';
import {
  Account,
  CareHomeShortlist,
  CaseManagerSummary,
  FieldType,
  PaginationParams,
} from '@karehero/models';
import {
  useLegacyCreateShortlistMutation,
  useLegacyMigrateShortlistMutation,
  useGetShortlistByCareProfileIdQuery,
  useLegacyUpdateShortlistMutation,
} from 'store/api/shortlist';
import { newToast } from 'store/slices/uiSlice';

// utils
import { getProfilePictureUrl } from '@karehero/llama/pwa/utils/getProfilePictureUrl';
import { getInitials, formatFullName } from '@karehero/helpers/name';

import { switchCareCircle } from 'store/slices/careCircleSlice';
import { AppDispatch } from 'store';
import { useAdminCaseload } from 'hooks/useAdminCaseload';

// types
interface CareCircleAccount extends Account {
  careCircleId: string;
  careProfileId: string;
  careRecipientFirstName?: string;
  caseManager?: CaseManagerSummary | null;
  watchers?: CaseManagerSummary[];
}

const shortlistFields = createFieldHelper<CareHomeShortlist>({
  shortlistURL: {
    label: 'Shortlist URL',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
    validationRules: [
      {
        type: 'min-length',
        value: 2,
      },
    ],
  },
  shortlistNote: {
    label: 'Shortlist Note',
    isRequired: true,
    field: {
      type: FieldType.Text,
    },
    validationRules: [
      {
        type: 'min-length',
        value: 2,
      },
    ],
  },
});

// columns
const columns: GridColDef[] = [
  {
    field: 'imgSrc',
    headerName: '',
    width: 72,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams) => {
      const row = params.row;
      const fallback =
        `${row.firstName?.[0] || ''}${row.lastName?.[0] || ''}`.toUpperCase();
      return (
        <Avatar size='xs' src={getProfilePictureUrl(row)} fallback={fallback} />
      );
    },
  },
  { field: 'fullName', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
  { field: 'kindeEmail', headerName: 'Kinde Email', flex: 1, minWidth: 180 },
  {
    field: 'careRecipientFirstName',
    headerName: 'Care Recipient Name',
    flex: 0.8,
    minWidth: 150,
    valueGetter: (_value: any, row: any) =>
      row.careRecipientFirstName || 'unknown',
  },
  {
    field: 'careCircleId',
    headerName: 'Care Circle ID',
    flex: 0.8,
    minWidth: 150,
  },
  {
    field: 'caseManager',
    headerName: 'Case Manager',
    flex: 0.8,
    minWidth: 160,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams) => {
      const cm = row.caseManager as CaseManagerSummary | null | undefined;
      if (!cm) {
        return (
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
            }}
          >
            Unassigned
          </Typography>
        );
      }
      const name = formatFullName(cm.firstName, cm.lastName);
      return (
        <Stack
          direction='row'
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <Chip size='small' label={getInitials(name)} />
          <Typography variant='body2'>{name}</Typography>
        </Stack>
      );
    },
  },
  {
    field: 'watchers',
    headerName: 'Watchers',
    flex: 0.6,
    minWidth: 120,
    sortable: false,
    renderCell: ({ row }: GridRenderCellParams) => {
      const watchers = row.watchers as CaseManagerSummary[] | undefined;
      if (!watchers?.length) return null;
      return (
        <Stack
          direction='row'
          spacing={0.5}
          sx={{
            alignItems: 'center',
          }}
        >
          {watchers.map((w) => (
            <Chip
              key={w.accountId}
              label={getInitials(formatFullName(w.firstName, w.lastName))}
              size='small'
            />
          ))}
        </Stack>
      );
    },
  },
];

/**
 * ManageCareCircles is the dashboard page.
 */
export const ManageCareCircles = () => {
  // state
  const [selectedProfile, setSelectedProfile] =
    useState<CareCircleAccount | null>(null);
  const [isUpdateShortlistFormOpen, setIsUpdateShortlistFormOpen] =
    useState(false);
  const [shortlist, setShortlist] = useState<Partial<CareHomeShortlist>>({});
  const [paginationParams, setPaginationParams] = useState<PaginationParams>({
    page: 1,
    perPage: 10,
  });

  // hooks
  const {
    data: careCirclesData,
    isFetching,
    isError,
  } = useGetAllQuery(paginationParams);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    hasCaseload,
    addToCaseload,
    removeFromCaseloadByCareCircle,
    getCaseStatus,
  } = useAdminCaseload();

  const { data: careHomeShortlist } = useGetShortlistByCareProfileIdQuery(
    selectedProfile?.careProfileId || '',
    { skip: !selectedProfile },
  );
  const [createShortlistLegacy] = useLegacyCreateShortlistMutation();
  const [migrateShortlistLegacy] = useLegacyMigrateShortlistMutation();
  const [updateShortlistLegacy] = useLegacyUpdateShortlistMutation();

  // effects
  useEffect(() => {
    if (!selectedProfile) return;

    if (selectedProfile.careProfileId !== careHomeShortlist?.careProfileID) {
      setShortlist({});
      return;
    }

    setShortlist(careHomeShortlist);
  }, [selectedProfile, careHomeShortlist, setShortlist]);

  // memos
  const rows = useMemo(() => {
    if (!careCirclesData?.data) return [];
    return careCirclesData.data.map((careCircle) => ({
      ...careCircle.primaryCaregiverAccount,
      careCircleId: careCircle.id,
      careProfileId: careCircle.careProfile?.id,
      careRecipientFirstName: careCircle.careRecipientAccount?.firstName,
      caseManager: careCircle.caseManager,
      watchers: careCircle.watchers,
    }));
  }, [careCirclesData]);

  // pagination handlers
  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setPaginationParams((prev) => ({
        ...prev,
        page: page + 1,
        perPage: pageSize,
      }));
    },
    [],
  );

  const handleSearchChange = useCallback((search: string) => {
    setPaginationParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleShortlist = useCallback(
    async (profile: CareCircleAccount) => {
      if (!profile) return;

      const res = shortlist.id
        ? await updateShortlistLegacy({
            ...shortlist,
            id: shortlist.id,
            careProfileID: profile.careProfileId,
          })
        : await createShortlistLegacy({
            ...shortlist,
            careProfileID: profile.careProfileId,
          });

      if ('error' in res) {
        console.error(res.error);
        dispatch(
          newToast({
            title: 'Error',
            description: 'An error occurred while processing the shortlist.',
            variant: 'error',
          }),
        );
        return;
      }

      dispatch(
        newToast({
          title: shortlist.id ? 'Shortlist Updated' : 'Shortlist Created',
          description: shortlist.id
            ? 'Shortlist successfully updated'
            : 'Shortlist successfully created',
        }),
      );

      setIsUpdateShortlistFormOpen(false);
    },
    [
      shortlist,
      setIsUpdateShortlistFormOpen,
      dispatch,
      updateShortlistLegacy,
      createShortlistLegacy,
    ],
  );

  const handleMigrateShortlist = useCallback(
    async (careProfileID: string, shortlistID: string) => {
      if (!careProfileID || !shortlistID) return;

      const res = await migrateShortlistLegacy({ careProfileID, shortlistID });

      if ('error' in res) {
        console.error(res.error);
        dispatch(
          newToast({
            title: 'Error',
            description: 'An error occurred while migrating the shortlist.',
            variant: 'error',
          }),
        );
        return;
      }

      dispatch(
        newToast({
          title: 'Shortlist Migrated',
          description: 'Shortlist successfully migrated',
        }),
      );

      setIsUpdateShortlistFormOpen(false);
    },
    [
      shortlist,
      setIsUpdateShortlistFormOpen,
      dispatch,
      updateShortlistLegacy,
      createShortlistLegacy,
    ],
  );
  return (
    <AdminTablePage title='Manage Care Circles'>
      <DataTable
        columns={columns}
        rows={rows}
        paginationMode='server'
        rowCount={careCirclesData?.pagination?.totalItems}
        page={(paginationParams.page ?? 1) - 1}
        onPaginationChange={handlePaginationChange}
        rowsPerPage={paginationParams.perPage ?? 10}
        isLoading={isFetching}
        isError={isError}
        onSearchChange={handleSearchChange}
        searchValue={paginationParams.search ?? ''}
        actions={[
          {
            label: 'View Profile Summary',
            icon: 'memo',
            onPress: async (careCircle: CareCircleAccount) => {
              navigate(`/admin/care-circle/view/${careCircle.careCircleId}`);
            },
          },
          {
            label: 'View As User',
            icon: 'user',
            onPress: async (careCircle: CareCircleAccount) => {
              await dispatch(switchCareCircle(careCircle.careCircleId));
              navigate('/');
            },
          },
          {
            label: 'Manage Shortlist',
            icon: 'list',
            onPress: (careCircle: CareCircleAccount) => {
              setSelectedProfile(careCircle);
              setIsUpdateShortlistFormOpen(true);
            },
          },
          {
            label: 'Call Notes',
            icon: 'phone',
            onPress: (careCircle: CareCircleAccount) => {
              navigate(
                `/admin/care-circle/${careCircle.careCircleId}/call-notes`,
              );
            },
          },
          ...(hasCaseload
            ? [
                {
                  label: 'Add to Caseload',
                  icon: 'bookmark' as const,
                  isHidden: (careCircle: CareCircleAccount) =>
                    !!getCaseStatus(careCircle.careCircleId),
                  onPress: async (careCircle: CareCircleAccount) => {
                    await addToCaseload(careCircle.careCircleId);
                  },
                },
                {
                  label: 'Remove from Caseload',
                  icon: 'bookmark' as const,
                  iconType: 'fas' as const,
                  isHidden: (careCircle: CareCircleAccount) =>
                    !getCaseStatus(careCircle.careCircleId),
                  onPress: async (careCircle: CareCircleAccount) => {
                    await removeFromCaseloadByCareCircle(
                      careCircle.careCircleId,
                    );
                  },
                },
              ]
            : []),
        ]}
      />
      <Dialog
        variant='dialog'
        isOpen={isUpdateShortlistFormOpen}
        setIsOpen={setIsUpdateShortlistFormOpen}
        title='Add shortlist URL'
        description={`${selectedProfile?.email}`}
        zIndex={2}
      >
        <Button
          variant='contained'
          color='warning'
          disabled={!selectedProfile || !shortlist.id}
          onClick={async () => {
            if (!selectedProfile || !shortlist.id) return;

            await handleMigrateShortlist(
              selectedProfile?.careProfileId,
              shortlist.id,
            );
          }}
        >
          Migrate Shortlist
        </Button>
        <Stack spacing={2}>
          <Editor
            onSave={() => {
              if (!selectedProfile) return;
              handleShortlist(selectedProfile);
            }}
            onCancel={() => setIsUpdateShortlistFormOpen(false)}
            saveLabel='Save'
            cancelLabel='Cancel'
            isWithArrows={false}
            isActionsDisabled={false}
            value={shortlist}
            onChange={(val: any) => setShortlist(val)}
            fields={shortlistFields}
            isDisabled={false}
            isFloatingActions={true}
            isForm={true}
            isSaveDisabled={false}
          />
        </Stack>
      </Dialog>
    </AdminTablePage>
  );
};

export default ManageCareCircles;
